import { useEffect, useMemo, useState } from 'react';
import type { TableOfContentItem } from 'graphql-ts-types';

import { EventBusEvent } from 'common/enums/EventBus';
import eventBus from 'common/event-bus/EventBus';

const buildAnchorWithQueryParams = (anchor: string, queryParams: string): string => {
  if (!queryParams) return anchor;

  // check if anchor is just a hash (e.g., "#section-name")
  if (anchor.startsWith('#')) {
    return `${queryParams}${anchor}`;
  }

  // handle anchors with paths (e.g., "moebel-sortiment/#section-name")
  const hashIndex = anchor.indexOf('#');
  if (hashIndex !== -1) {
    const path = anchor.slice(0, hashIndex);
    const hash = anchor.slice(hashIndex);

    return `${path}${queryParams}${hash}`;
  }

  // If no hash, append query params at the end
  return `${anchor}${queryParams}`;
};

export const usePreserveQueryParams = (
  headings: Array<Pick<TableOfContentItem, 'name' | 'anchor'>>
) => {
  const [queryParams, setQueryParams] = useState('');

  const processedHeadings = useMemo(
    () =>
      headings.map(heading => ({
        ...heading,
        anchor: buildAnchorWithQueryParams(heading.anchor, queryParams),
      })),
    [headings, queryParams]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateQueryParams = () => {
      setQueryParams(window.location.search);
    };

    updateQueryParams();

    eventBus.on(EventBusEvent.UrlUpdate, updateQueryParams);
    window.addEventListener('popstate', updateQueryParams);

    return () => {
      eventBus.off(EventBusEvent.UrlUpdate, updateQueryParams);
      window.removeEventListener('popstate', updateQueryParams);
    };
  }, []);

  return processedHeadings;
};
